import { useEffect, useState } from "react"
import { AutoComplete } from "@components";
import { GET_LESSON_AUTO_COMPLETES } from "@graphql"
import { staffFullname } from '@helpers'
import { useQuery } from "@apollo/client"
import { BookOpenIcon } from "@heroicons/react/24/outline";
import { Chip } from "@heroui/react";
import PropTypes from 'prop-types';

const LessonAutoComplete = ({ clientId, inputRef, onChange, value }) => {
  const [currentPage, setCurrentPage] = useState(1)
  const [perPage] = useState(50)
  const [searchTerm, setSearchTerm] = useState("")
  const [skip, setSkip] = useState(true)
  const [querySearchTerm, setQuerySearchTerm] = useState("")
  const [lessons, setLessons] = useState([])

  useEffect(() => {
    setSearchTerm(value?.name || "")
  }, [value])

  useEffect(() => {
    const debounce = setTimeout(() => setQuerySearchTerm(searchTerm), 300)
    return () => clearTimeout(debounce)
  }, [searchTerm])

  const {loading, data } = useQuery(GET_LESSON_AUTO_COMPLETES, {
    variables: { 
      clientId, 
      searchTerm: querySearchTerm, 
      page: currentPage, 
      perPage, 
      orderBy: [{ "column": "NAME", "order": "ASC" }] 
    },
    fetchPolicy: "cache-first",
    skip: skip || querySearchTerm.length === 0,
    onCompleted: (data) => {
      currentPage === 1 
        ? setLessons(data.client.lessons.data) 
        : setLessons((prevLessons) => ([...prevLessons, ...data.client.lessons.data]))
    }
  })

  const getOptions = () => {
    if (querySearchTerm.length === 0) return []
    
    return lessons.map((lesson) => {
      const subject = lesson?.subject ? lesson.subject.name : ""
      const teacher = lesson?.teacher ? staffFullname(lesson?.teacher) : "No Teacher Assigned"

      return {
        id: lesson.id,
        title: <div className="flex justify-between">
          <span>{lesson.name}</span>
          
          <Chip 
            classNames={{ base: `font-semibold ${teacher === "No Teacher Assigned" ? "bg-orange-500" : "bg-lime-500"}`, content: "text-white" }} 
            size="sm"
          >
            {teacher}
          </Chip>
        </div>,
        lesson: {...lesson},
        icon: null,
        description: <div>{subject && <span className="flex items-center"><BookOpenIcon className="h-5 w-5 mr-1.5" />{subject}</span>}</div>
      }
    })
  }

  const handleSelectLesson = (lesson) => {
    onChange(lesson.lesson)
    setSkip(true)
    setSearchTerm(lesson.name)
  }

  const handleSearchChange = (searchTerm) => {
    onChange(null)
    setSkip(false)
    setSearchTerm(searchTerm)
    setCurrentPage(1)
  }

  const handleSearchClear = () => {
    setSearchTerm("")
    onChange(null)
    setSkip(true)
  }

  const options = getOptions()

  return ( 
    <AutoComplete 
      handleChange={(searchTerm) => handleSearchChange(searchTerm)}
      handleClear={handleSearchClear}
      handleLoadMore={() => setCurrentPage(currentPage + 1)}
      handleOptionSelected={(selectedOption) => handleSelectLesson(selectedOption)}
      hasMore={data?.client.lessons.paginatorInfo.hasMorePages}
      id="lessons-auto-complete"
      inputRef={inputRef}
      isLoading={loading || (searchTerm !== querySearchTerm && searchTerm !== "")}
      label="Select a lesson"
      noData={{ title: "No lessons found", message: "Sorry no lessons found. Try adjusting or clearing your filter." }}
      placeholder="Lesson name / subject or teacher..."
      searchTerm={searchTerm} 
      isSelected={value !== null}
      options={options}
    />      
  )
}

LessonAutoComplete.propTypes = {
  clientId: PropTypes.string.isRequired,
  inputRef: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  value: PropTypes.object
}

export default LessonAutoComplete